import { FC, useState, useId } from 'react';
import { makeStyles, shorthands, tokens, Input, Textarea, Button, Label, Text } from '@fluentui/react-components';
import { ClipboardTaskListLtr24Regular, Dismiss24Regular, Edit24Regular } from '@fluentui/react-icons';
import { 
    CircleAlert,
    AlertCircle
} from 'lucide-react';

export interface SurveyMiniFormProps {
    onClose: () => void;
    onFormDataChange: (data: SurveyFormData) => void;
    mode?: 'create' | 'edit';
    initialData?: SurveyFormData;
    // Optional error message to display under the survey name input (e.g. duplicate name)
    errorMessage?: string | null;
    // Called when the name input changes (used to clear validation errors in the parent)
    onNameChange?: () => void;
}

export interface SurveyFormData {
    surveyName: string;
    surveySubtitle: string;
    surveyDescription: string;
}

const useStyles = makeStyles({
    container: {
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...shorthands.padding('40px', '40px', '20px', '40px'),
        ...shorthands.gap('20px'),
        width: '100%',
        height: '100%',
        position: 'relative',
    },
    content: {
        display: 'flex',
        ...shorthands.gap('10px'),
        alignItems: 'flex-start',
        width: '100%',
    },
    icon: {
        flexShrink: 0,
        width: '24px',
        height: '24px',
        color: tokens.colorNeutralForeground1,
    },
    title: {
        fontWeight: tokens.fontWeightBold,
        fontSize: '20px',
        lineHeight: 'normal',
        color: tokens.colorNeutralForeground1,
        letterSpacing: '0.1px',
        whiteSpace: 'nowrap',
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('16px'),
        width: '100%',
    },
    inputField: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('8px'),
        width: '100%',
    },
    inputFieldTextarea: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('8px'),
        width: '100%',
        height: '118px',
    },
    label: {
        color: tokens.colorNeutralForeground1,
        // typography moved to CSS so we don't repeat size/weight inline
        fontSize: '14px',
        lineHeight: '20px',
        fontWeight: tokens.fontWeightSemibold,
    },
    errorText: {
        color: tokens.colorPaletteRedForeground1,
        marginTop: '0px',
    },
    inputError: {
        ...shorthands.border('1px', 'solid', '#ef4444'),
        ...shorthands.borderRadius('8px'),
        boxShadow: `0 0 0 1px #ef4444 inset`,
        '& input': {
            ...shorthands.padding('14px', '16px'),
            paddingRight: '44px',
            ...shorthands.border('1px', 'solid', '#ef4444'),
            ...shorthands.borderRadius('6px'),
        },
    },
    inputContainer: {
        position: 'relative',
        width: '100%',
    },
    errorIcon: {
        position: 'absolute',
        right: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: tokens.colorPaletteRedForeground1,
        pointerEvents: 'none',
        width: '16px',
        height: '16px',
    },
    requiredMark: {
        color: '#ef4444',
    },
    input: {
        ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
        ...shorthands.borderRadius('8px'),
        width: '100%',
        '& input': {
            ...shorthands.padding('14px', '16px'),
        },
        // placeholder color is handled globally via CSS variables
    },
    textarea: {
        ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
        ...shorthands.borderRadius('8px'),
        width: '100%',
        flexGrow: 1,
        minHeight: '92px',
        '& textarea': {
            ...shorthands.padding('14px', '16px'),
            minHeight: '92px',
        },
        // placeholder color is handled globally via CSS variables
    },
    closeButton: {
        position: 'absolute',
        top: '10px',
        right: '10px',
        cursor: 'pointer',
        ...shorthands.padding(0),
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...shorthands.borderRadius('8px'),
        width: '38px',
        height: '38px',
        backgroundColor: 'transparent',
        ...shorthands.border('none'),
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
        '&:active': {
            backgroundColor: tokens.colorNeutralBackground1Pressed,
        },
    },
    closeIcon: {
        width: '16px',
        height: '16px',
        color: tokens.colorNeutralForeground1,
    },
});

const SurveyMiniForm: FC<SurveyMiniFormProps> = ({ onClose, onFormDataChange, mode = 'create', initialData, errorMessage, onNameChange }) => {
    const styles = useStyles();
    const nameErrorId = useId();
    // small wrapper components to keep typography consistent and avoid repeating props
    const TitleText: FC<{ children: React.ReactNode }> = ({ children }) => (
        <Text className={styles.title}>{children}</Text>
    );

    const LabelText: FC<{ children: React.ReactNode }> = ({ children }) => (
        <Text className={styles.label}>{children}</Text>
    );
    const [surveyName, setSurveyName] = useState(initialData?.surveyName || '');
    const [surveySubtitle, setSurveySubtitle] = useState(initialData?.surveySubtitle || '');
    const [surveyDescription, setSurveyDescription] = useState(initialData?.surveyDescription || '');

    // Notify parent of form data changes
    const updateFormData = (name: string, subtitle: string, description: string) => {
        onFormDataChange({
            surveyName: name,
            surveySubtitle: subtitle,
            surveyDescription: description,
        });
    };

    return (
        <div className={styles.container} data-name="Body">
            <div className={styles.content} data-name="Content">
                {mode === 'edit' ? (
                    <Edit24Regular className={styles.icon} />
                ) : (
                    <ClipboardTaskListLtr24Regular className={styles.icon} />
                )}
                <TitleText>{mode === 'edit' ? 'Edit survey details' : 'Create a new survey'}</TitleText>
            </div>
            <div className={styles.list} data-name="List">
                <div className={styles.inputField} data-name="input-field">
                    <LabelText>What is your survey name?<span className={styles.requiredMark}>*</span></LabelText>
                    <div className={styles.inputContainer}>
                        <Input
                            className={`${styles.input} ${errorMessage ? styles.inputError : ''}`}
                            placeholder="Enter a survey name"
                            value={surveyName}
                            onChange={(_, data) => {
                                setSurveyName(data.value);
                                updateFormData(data.value, surveySubtitle, surveyDescription);
                                // notify parent to clear any name-related validation error
                                if (onNameChange) onNameChange();
                            }}
                            style={errorMessage ? { borderColor: tokens.colorPaletteRedBorder1, borderWidth: '2px' } : undefined}
                            aria-invalid={errorMessage ? true : undefined}
                            aria-describedby={errorMessage ? nameErrorId : undefined}
                        />
                        {errorMessage && <AlertCircle className={styles.errorIcon} />}
                    </div>
                    {errorMessage && (
                        <Text id={nameErrorId} size={400} weight="semibold" className={styles.errorText}>{errorMessage}</Text>
                    )}
                </div>
                <div className={styles.inputField} data-name="input-field">
                    <LabelText>What is the subtitle of the survey?<span className={styles.requiredMark}>*</span></LabelText>
                    <Input
                        className={styles.input}
                        placeholder="We would love to hear your feedback..."
                        value={surveySubtitle}
                        onChange={(_, data) => {
                            setSurveySubtitle(data.value);
                            updateFormData(surveyName, data.value, surveyDescription);
                        }}
                    />
                </div>
                <div className={styles.inputFieldTextarea} data-name="input-field">
                    <LabelText>What is your survey about ?<span className={styles.requiredMark}>*</span></LabelText>
                    <Textarea
                        className={styles.textarea}
                        placeholder="Describe here your project..."
                        value={surveyDescription}
                        onChange={(_, data) => {
                            setSurveyDescription(data.value);
                            updateFormData(surveyName, surveySubtitle, data.value);
                        }}
                        resize="vertical"
                    />
                </div>
            </div>
            <button className={styles.closeButton} onClick={onClose} aria-label="Close">
                <Dismiss24Regular className={styles.closeIcon} />
            </button>
        </div>
    );
};

export default SurveyMiniForm;
